const COMPACT_RETAIL_CODE_PATTERN = /^(?=.*\d)[a-z0-9]{4,64}$/;

export const RETAIL_SCAN_IDLE_MS = 100;
export const RETAIL_SCAN_MIN_LENGTH = 4;
export const RETAIL_SCAN_MAX_AVERAGE_INTERVAL_MS = 50;

export const createEmptyRetailScanBurst = () => ({
    value: "",
    startedAt: 0,
    lastObservedAt: 0,
    characterCount: 0,
    pasted: false,
});

export const updateRetailScanBurst = (
    previousBurst,
    nextValue,
    observedAt = Date.now()
) => {
    const previous = previousBurst || createEmptyRetailScanBurst();
    const value = String(nextValue || "");
    if (!value) return createEmptyRetailScanBurst();

    const continuesPreviousValue =
        Boolean(previous.value) &&
        value.startsWith(previous.value) &&
        value.length > previous.value.length;
    const elapsedSincePrevious =
        Number(observedAt) - Number(previous.lastObservedAt || 0);
    const continuesBurst =
        continuesPreviousValue &&
        elapsedSincePrevious >= 0 &&
        elapsedSincePrevious <= RETAIL_SCAN_IDLE_MS;

    if (!continuesBurst) {
        const insertedCharacters = previous.value
            ? Math.max(value.length - previous.value.length, 1)
            : value.length;
        return {
            value,
            startedAt: Number(observedAt),
            lastObservedAt: Number(observedAt),
            characterCount: insertedCharacters,
            pasted: !previous.value && value.length > 1,
        };
    }

    const insertedCharacters = value.length - previous.value.length;
    return {
        value,
        startedAt: Number(previous.startedAt),
        lastObservedAt: Number(observedAt),
        characterCount: previous.characterCount + insertedCharacters,
        pasted: previous.pasted || insertedCharacters > 1,
    };
};

export const isLikelyRetailScannerBurst = (burst) => {
    const valueLength = String(burst?.value || "").trim().length;
    if (valueLength < RETAIL_SCAN_MIN_LENGTH) return false;
    if (burst?.pasted) return true;

    const characterCount = Number(burst?.characterCount || 0);
    if (characterCount < RETAIL_SCAN_MIN_LENGTH) return false;

    const elapsed =
        Number(burst?.lastObservedAt || 0) -
        Number(burst?.startedAt || 0);
    const intervalCount = Math.max(characterCount - 1, 1);
    return (
        elapsed >= 0 &&
        elapsed / intervalCount <= RETAIL_SCAN_MAX_AVERAGE_INTERVAL_MS
    );
};

export const isRetailScanTerminator = (key) =>
    key === "Enter" || key === "Tab";

const compactItemCode = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

export const buildSeparatorTolerantRetailSearchTerm = (searchTerm) => {
    const compactSearchTerm = String(searchTerm || "").trim().toLowerCase();
    if (!COMPACT_RETAIL_CODE_PATTERN.test(compactSearchTerm)) return null;

    return compactSearchTerm.split("").join("%");
};

export const filterRetailItemsByCompactCode = (items, searchTerm) => {
    const compactSearchTerm = String(searchTerm || "").trim().toLowerCase();
    if (!COMPACT_RETAIL_CODE_PATTERN.test(compactSearchTerm)) return [];

    return (items || []).filter((item) =>
        compactItemCode(item?.item_code).startsWith(compactSearchTerm)
    );
};
